
import React from "react";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import ConnectionCard from "./ConnectionCard";
import EmptyState from "./EmptyState";
import ProfileViewsSection from "@/components/profile-views/ProfileViewsSection";
import BlurredConnectionsOverlay from "./BlurredConnectionsOverlay";
import { BaseConnection } from "@/types/connections";

interface ConnectionTabsProps {
  sentConnections: BaseConnection[];
  receivedConnections: BaseConnection[];
  matches: BaseConnection[];
  onProfileAction: (id: string | number, actionType: 'like' | 'unlike' | 'message') => void;
  activeTab: "sent" | "received" | "matches" | "views";
  onTabChange: (tab: "sent" | "received" | "matches" | "views") => void;
  loading: boolean;
  viewsCount?: number;
  canLike?: boolean;
  likeLimits?: {
    current: number;
    limit: number;
    remaining: number;
    isUnlimited: boolean;
  };
  shouldShowViewsBlur?: boolean;
}

const ConnectionTabs: React.FC<ConnectionTabsProps> = ({
  sentConnections,
  receivedConnections,
  matches,
  onProfileAction,
  activeTab,
  loading,
  shouldShowViewsBlur = false
}) => {
  const tabs = [
    {
      value: "received",
      data: receivedConnections
    },
    {
      value: "sent", 
      data: sentConnections
    },
    {
      value: "matches",
      data: matches
    },
    {
      value: "views",
      data: []
    }
  ];

  if (loading) {
    return (
      <div className="py-8">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-gray-800 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 relative">
      <Tabs value={activeTab}>
        {tabs.slice(0, 3).map((tab) => (
          <TabsContent key={tab.value} value={tab.value} className="mt-0">
            {tab.data.length === 0 ? (
              <EmptyState type={tab.value === "matches" ? "matches" : tab.value as "received" | "sent"} />
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {tab.data.map((connection) => (
                  <ConnectionCard
                    key={connection.id} 
                    profile={{
                      id: connection.id,
                      name: connection.username,
                      username: connection.username,
                      age: connection.age,
                      location: connection.location,
                      photo: connection.main_photo_url,
                      occupation: connection.occupation,
                      interests: connection.interests,
                      mutual: connection.mutual,
                      connectionDate: connection.connectionDate
                    }}
                    isMatch={tab.value === "matches"}
                    isSentLike={tab.value === "sent"}
                    isReceived={tab.value === "received"}
                    onAction={onProfileAction}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        ))}

        {/* Views tab content with conditional blur overlay */}
        <TabsContent value="views" className="mt-0 relative min-h-[80vh] pb-24">
          <div className={shouldShowViewsBlur ? 'pointer-events-none' : ''}>
            <ProfileViewsSection />
          </div>
          
          {/* Blurred overlay only for views tab */}
          <BlurredConnectionsOverlay isVisible={shouldShowViewsBlur} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ConnectionTabs;
